import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { normalizeImageUrl, productImage } from "@/lib/imageUrl";
import { X, ShoppingCart, Trash2, Package, Tag, AlertCircle } from "lucide-react";

export const money = (c) => `$${(((c || 0) / 100)).toFixed(2)}`;

export function CartButton({ mobile = false }) {
  const { count, setOpen } = useCart();
  return (
    <button data-testid={mobile ? "mobile-cart-button" : "cart-button"} onClick={() => setOpen(true)}
      className={`relative ${mobile ? "p-2 text-[#0A192F]" : "flex items-center justify-center w-10 h-10 border border-slate-200 rounded-sm text-[#0A192F] hover:border-slate-300"}`}
      aria-label="Cart">
      <ShoppingCart className="w-5 h-5" />
      {count > 0 && (
        <span data-testid="cart-count-badge" className="absolute -top-1.5 -right-1.5 bg-[#E63946] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] grid place-items-center px-1">{count}</span>
      )}
    </button>
  );
}

function DiscountRow() {
  const { discountCode, setDiscountCode, quote } = useCart();
  const [val, setVal] = useState(discountCode);
  const d = quote?.discount;
  return (
    <div data-testid="cart-discount">
      <div className="flex gap-2">
        <div className="flex items-center border border-slate-300 rounded-sm bg-white flex-1">
          <Tag className="w-4 h-4 text-slate-400 ml-2" />
          <input data-testid="discount-input" value={val} onChange={(e) => setVal(e.target.value.toUpperCase())}
            placeholder="Discount code" className="flex-1 px-2 py-2 text-sm outline-none uppercase" />
        </div>
        <button data-testid="apply-discount" onClick={() => setDiscountCode(val.trim())}
          className="px-4 py-2 bg-[#0A192F] text-white text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-[#1a2a4a]">Apply</button>
      </div>
      {discountCode && d && d.error && <div data-testid="discount-error" className="mt-1.5 text-xs text-[#E63946] flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {d.error}</div>}
      {discountCode && d && d.valid && (
        <div data-testid="discount-applied" className="mt-1.5 text-xs text-emerald-600 flex items-center justify-between">
          <span>Code {d.code} applied{d.description ? ` — ${d.description}` : ""}</span>
          <button onClick={() => { setDiscountCode(""); setVal(""); }} className="text-slate-400 hover:text-[#E63946]">Remove</button>
        </div>
      )}
    </div>
  );
}

function ShippingTaxControls({ config }) {
  const { country, setCountry, province, setProvince, shippingMethod, setShippingMethod, quote } = useCart();
  const opts = quote?.shipping?.options || [];
  const taxEnabled = config?.tax?.enabled;
  const regions = config?.tax?.regions || [];
  return (
    <div className="space-y-3">
      <div>
        <div className="label-overline text-[10px] mb-1.5">Destination</div>
        <div className="flex gap-2">
          <select data-testid="cart-country" value={country} onChange={(e) => setCountry(e.target.value)}
            className="border border-slate-300 rounded-sm px-2 py-2 text-sm bg-white flex-1">
            <option value="CA">Canada</option>
            <option value="US">United States</option>
          </select>
          {taxEnabled && country === "CA" && (
            <select data-testid="cart-province" value={province} onChange={(e) => setProvince(e.target.value)}
              className="border border-slate-300 rounded-sm px-2 py-2 text-sm bg-white flex-1">
              <option value="">Province…</option>
              {regions.map((r) => <option key={r.region} value={r.region}>{r.region}</option>)}
            </select>
          )}
        </div>
      </div>
      {opts.length > 0 && (
        <div>
          <div className="label-overline text-[10px] mb-1.5">Shipping method</div>
          <div className="space-y-1.5">
            {opts.map((o) => (
              <label key={o.id} data-testid={`ship-option-${o.id}`} className={`flex items-center justify-between border rounded-sm px-3 py-2 text-sm cursor-pointer ${quote?.shipping?.method === o.id ? "border-[#0A192F] bg-slate-50" : "border-slate-200"}`}>
                <span className="flex items-center gap-2">
                  <input type="radio" name="ship" checked={quote?.shipping?.method === o.id} onChange={() => setShippingMethod(o.id)} />
                  {o.label}
                </span>
                <span className="font-data">{o.free ? "Free" : money(o.amount_cents)}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CartBody({ compact = false }) {
  const { items, quote, updateQty, removeItem, keyOf, loading } = useCart();
  const [config, setConfig] = useState(null);
  useEffect(() => { api.get("/marketplace/checkout-config").then(({ data }) => setConfig(data)).catch(() => {}); }, []);

  if (!items.length) {
    return (
      <div data-testid="cart-empty" className="text-center py-16 text-slate-400">
        <ShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="font-semibold">Your cart is empty.</p>
        <p className="text-sm">Add some official CBR gear to get started.</p>
      </div>
    );
  }
  const lines = quote?.lines || [];

  if (items.length && !quote) {
    return <div data-testid="cart-loading" className="py-16 text-center text-slate-400">Loading your cart…</div>;
  }

  return (
    <div className="space-y-4">
      {(quote?.notices || []).length > 0 && (
        <div data-testid="cart-notices" className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-800 space-y-1">
          {quote.notices.map((n, i) => <div key={i} className="flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {n.product ? `${n.product}: ` : ""}{n.reason}</div>)}
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {lines.map((l) => {
          const k = `${l.product_id}::${l.variant_id || ""}`;
          return (
            <div key={k} data-testid={`cart-line-${l.slug}`} className="flex gap-3 py-3">
              <div className="w-16 h-16 shrink-0 bg-slate-50 rounded-sm overflow-hidden border border-slate-200">
                <img src={productImage(l.image_url)} alt={l.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0A192F] leading-tight">{l.name}</div>
                {Object.keys(l.option_values || {}).length > 0 && (
                  <div className="text-xs text-slate-500 mt-0.5">{Object.entries(l.option_values).map(([, v]) => v).join(" · ")}</div>
                )}
                <div className="mt-1.5 flex items-center justify-between">
                  <div className="flex items-center border border-slate-300 rounded-sm">
                    <button data-testid={`cart-dec-${l.slug}`} onClick={() => updateQty(k, l.qty - 1)} className="px-2 py-1 text-slate-500">−</button>
                    <span data-testid={`cart-qty-${l.slug}`} className="px-2 text-sm font-data">{l.qty}</span>
                    <button data-testid={`cart-inc-${l.slug}`} onClick={() => updateQty(k, l.qty + 1)} className="px-2 py-1 text-slate-500">+</button>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-data text-[#0A192F]">{money(l.line_total_cents)}</span>
                    <button data-testid={`cart-remove-${l.slug}`} onClick={() => removeItem(k)} className="text-slate-400 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <DiscountRow />
      <div className="border-t border-slate-200 pt-4"><ShippingTaxControls config={config} /></div>

      {/* Totals */}
      <div className="border-t border-slate-200 pt-4 space-y-1.5 text-sm" data-testid="cart-totals">
        <Row label="Subtotal" value={money(quote?.subtotal_cents)} testid="total-subtotal" />
        {quote?.discount_cents > 0 && <Row label={`Discount${quote?.discount?.code ? ` (${quote.discount.code})` : ""}`} value={`− ${money(quote.discount_cents)}`} testid="total-discount" tone="text-emerald-600" />}
        <Row label={`Shipping${quote?.shipping?.label ? ` · ${quote.shipping.label}` : ""}`} value={quote?.shipping_cents ? money(quote.shipping_cents) : "Free"} testid="total-shipping" />
        {quote?.tax?.enabled && quote?.tax_cents > 0 && <Row label={`Tax${quote?.tax?.name ? ` (${quote.tax.name})` : ""}`} value={money(quote.tax_cents)} testid="total-tax" />}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-1">
          <span className="font-semibold text-[#0A192F]">Total</span>
          <span data-testid="total-grand" className="font-data text-lg text-[#0A192F]">{money(quote?.total_cents)}</span>
        </div>
        {!quote?.tax?.enabled && <div className="text-[11px] text-slate-400">Taxes calculated at checkout where applicable.</div>}
      </div>
      {loading && <div className="text-xs text-slate-400" data-testid="cart-updating">Updating…</div>}
    </div>
  );
}

function Row({ label, value, testid, tone = "text-slate-600" }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={tone}>{label}</span>
      <span className={`font-data ${tone}`}>{value}</span>
    </div>
  );
}

export default function CartDrawer() {
  const { open, setOpen, quote, items } = useCart();
  const navigate = useNavigate();
  if (!open) return null;
  const goCheckout = () => { setOpen(false); navigate("/marketplace/checkout"); };
  return (
    <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <span className="font-display text-lg uppercase tracking-tight flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> Your Cart</span>
          <button data-testid="cart-close" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4"><CartBody compact /></div>
        {items.length > 0 && (
          <div className="border-t border-slate-200 p-5">
            <button data-testid="cart-checkout" onClick={goCheckout} disabled={!quote?.total_cents}
              className="w-full bg-[#E63946] hover:bg-[#c62f3b] disabled:opacity-50 text-white py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
              Checkout · {money(quote?.total_cents)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
